import { ProductBrand } from '@aide/ui'
import { useState, useRef, useEffect } from 'react'

import {
  MessageSquare,
  Plus,
  LogOut,
  UserCircle2,
  ChevronDown,
  FolderOpen,
  Trash2,
  Pencil,
  PanelLeftClose,
  PanelLeft,
  Settings,
  Check,
  X,
  Brain,
  Database,
  Sun,
  Moon,
  Briefcase,
  Sparkles,
} from 'lucide-react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { useChatStore } from '@/store/chatStore'
import PersonaSwitcher from './PersonaSwitcher'
import { cn } from '@/lib/utils'
import api, { type ProjectData } from '@/services/api'

const SERVER_PROJECT_LIMIT = 500

function CollapsedThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme()
  return (
    <button
      type="button"
      onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
      className="h-9 w-9 rounded-lg border border-border bg-transparent text-foreground/80 hover:text-primary flex items-center justify-center"
      title="Toggle theme"
    >
      {resolvedTheme === 'dark' ? (
        <Sun className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4" />
      )}
    </button>
  )
}

function UserMenu({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!user) return null

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-foreground/80 hover:bg-muted/60 transition-colors"
      >
        <UserCircle2 className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1 text-left">{user}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="px-3 py-2.5 border-b border-border">
            <div className="text-[11px] text-muted-foreground">Signed in as</div>
            <div className="text-sm font-medium text-foreground truncate">{user}</div>
          </div>

          <div className="py-1">
            <button
              type="button"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-foreground/80 hover:bg-muted/60 transition-colors"
              onClick={() => { onNavigate('/admin/settings'); setOpen(false) }}
            >
              <Settings className="h-4 w-4 shrink-0" />
              Settings
            </button>

            <button
              type="button"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-foreground/80 hover:bg-muted/60 transition-colors"
              onClick={() => {
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
              }}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="h-4 w-4 shrink-0" />
              ) : (
                <Moon className="h-4 w-4 shrink-0" />
              )}
              {resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>
          </div>

          <div className="border-t border-border py-1">
            <button
              type="button"
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              onClick={() => { logout(); setOpen(false) }}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function InlineEdit({
  value,
  onSave,
  onCancel,
}: {
  value: string
  onSave: (v: string) => void
  onCancel: () => void
}) {
  const [text, setText] = useState(value)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  return (
    <div className="flex items-center gap-1">
      <input
        ref={ref}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onSave(text.trim() || value)
          if (e.key === 'Escape') onCancel()
        }}
        className="flex-1 min-w-0 bg-transparent border border-border rounded px-1.5 py-0.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <button type="button" onClick={() => onSave(text.trim() || value)} className="text-primary hover:text-primary/80">
        <Check className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export default function SideNav() {
  const { profile } = useAuth()
  const tenantId = profile?.authenticated ? profile.tenant_id : undefined
  const userId = profile?.authenticated ? profile.id : undefined
  // Server discovery is ephemeral and remounted at every identity boundary.
  return <ScopedSideNav key={JSON.stringify([tenantId, userId])} tenantId={tenantId} userId={userId} />
}

function ScopedSideNav({ tenantId, userId }: { tenantId?: string | null; userId?: string | null }) {
  const {
    projects,
    activeProjectId,
    activeConversationId,
    sidebarExpanded,
    createProject,
    deleteProject,
    renameProject,
    setActiveConversation,
    deleteConversation,
    renameConversation,
    setSidebarExpanded,
    setActiveProjectId,
    getProjectConversations,
  } = useChatStore()

  const navigate = useNavigate()
  const location = useLocation()
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [editingConvoId, setEditingConvoId] = useState<string | null>(null)
  const [creatingProject, setCreatingProject] = useState(false)
  const [serverProjects, setServerProjects] = useState<ProjectData[]>([])
  const [routeProject, setRouteProject] = useState<ProjectData | null>(null)
  const [projectStatus, setProjectStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [refreshProjects, setRefreshProjects] = useState(0)
  const routeProjectId = location.pathname.match(/^\/p\/([^/]+)(?:\/|$)/)?.[1]
  const selectedProjectId = routeProjectId || activeProjectId

  useEffect(() => {
    if (!tenantId || !userId) return
    let active = true
    setProjectStatus('loading')
    api.listProjects().then(result => {
      if (!active) return
      setServerProjects(result.projects.filter(project => project.tenant_id === tenantId).slice(0, SERVER_PROJECT_LIMIT))
      setProjectStatus('ready')
    }).catch(() => { if (active) setProjectStatus('error') })
    return () => { active = false }
  }, [tenantId, userId, refreshProjects])

  useEffect(() => {
    setRouteProject(null)
    if (!tenantId || !userId || !routeProjectId || routeProjectId === 'default') return
    let active = true
    // Resolve a directly linked project even when it falls outside the bounded list.
    api.getProject(routeProjectId).then(project => {
      if (active && project.tenant_id === tenantId && project.project_id === routeProjectId) setRouteProject(project)
    }).catch(() => { /* The list still works when the route is unavailable. */ })
    return () => { active = false }
  }, [tenantId, userId, routeProjectId])

  const discoveredProjects = [...serverProjects]
  if (routeProject && routeProject.project_id === routeProjectId && !discoveredProjects.some(project => project.project_id === routeProject.project_id)) {
    discoveredProjects.push(routeProject)
  }
  const serverProjectIds = new Set(discoveredProjects.map(project => project.project_id))
  const displayProjects = [
    ...projects.filter(project => !serverProjectIds.has(project.id)),
    ...discoveredProjects.map(project => ({ id: project.project_id, name: project.name })),
  ]

  const activeProject = displayProjects.find(p => p.id === selectedProjectId)
  const projectConversations = activeProject ? getProjectConversations(activeProject.id) : []

  const handleNewChat = () => {
    setActiveProjectId(selectedProjectId)
    setActiveConversation(null)
    navigate(selectedProjectId ? `/p/${encodeURIComponent(selectedProjectId)}` : '/', { replace: true })
  }

  const handleNewProject = (name: string) => {
    if (!name.trim()) {
      setCreatingProject(false)
      return
    }
    createProject(name.trim())
    setCreatingProject(false)
  }

  if (!sidebarExpanded) {
    return (
      <aside className="fixed inset-y-0 left-0 z-30 w-14 md:w-16 border-r border-border bg-card/70 flex flex-col items-center py-3 md:py-4">
        <div className="mb-4 flex items-center justify-center w-9 h-9">
          <span
            className="text-foreground font-black text-[1.4rem] leading-none select-none"
            style={{ fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif' }}
          >
            C
          </span>
        </div>

        <nav className="flex flex-col items-center gap-2 w-full">
          <button
            type="button"
            title="Expand sidebar"
            onClick={() => setSidebarExpanded(true)}
            className="h-9 w-9 rounded-lg border border-border bg-transparent text-foreground/80 hover:text-primary flex items-center justify-center"
            aria-label="Expand sidebar"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Ask Contextful"
            onClick={handleNewChat}
            className="h-9 w-9 rounded-lg border border-border bg-transparent text-foreground/80 hover:text-primary flex items-center justify-center"
            aria-label="Ask Contextful"
          >
            <Plus className="h-4 w-4" />
          </button>
        </nav>

        <div className="mt-auto flex flex-col items-center gap-2 w-full px-2">
          <CollapsedThemeToggle />
        </div>
      </aside>
    )
  }

  return (
    <aside className="fixed inset-y-0 left-0 z-30 w-60 border-r border-border bg-card/95 backdrop-blur-sm flex flex-col transition-all duration-200">
      {/* Header */}
      <div className="flex items-center px-4 py-3.5 border-b border-border">
        <ProductBrand product="contextful" />
        <button
          type="button"
          onClick={() => setSidebarExpanded(false)}
          className="ml-auto h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center justify-center transition-colors"
          aria-label="Collapse sidebar"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Persona switcher + New chat */}
      <div className="px-2 pt-2 space-y-2">
        {activeProject && (
          <div className="flex justify-center">
            <PersonaSwitcher />
          </div>
        )}
        <button
          type="button"
          onClick={handleNewChat}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-foreground border border-border hover:bg-muted/60 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Ask Contextful
        </button>
      </div>

      {/* Projects section */}
      <div className="px-2 pt-3">
        <div className="flex items-center justify-between px-1 mb-1">
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Knowledge spaces</span>
          <button
            type="button"
            onClick={() => setCreatingProject(true)}
            className="h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center justify-center"
            title="New knowledge space"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>

        {creatingProject && (
          <div className="px-1 mb-1">
            <InlineEdit
              value=""
              onSave={handleNewProject}
              onCancel={() => setCreatingProject(false)}
            />
          </div>
        )}

        {tenantId && userId && projectStatus === 'loading' && <p role="status" className="px-2 py-1 text-xs text-muted-foreground">Loading server projects…</p>}
        {projectStatus === 'error' && <div className="px-2 py-1 text-xs">
          <p role="alert">Server projects could not be loaded.</p>
          <button type="button" className="mt-1 underline" onClick={() => setRefreshProjects(value => value + 1)}>Retry projects</button>
        </div>}
        {serverProjects.length >= SERVER_PROJECT_LIMIT && <p className="px-2 py-1 text-xs text-muted-foreground">Showing the first 500 server projects. Direct project links also work.</p>}
        <div className="max-h-[40vh] overflow-y-auto space-y-0.5">
          {displayProjects.map(project => (
            <div key={project.id} className="group">
              {editingProjectId === project.id ? (
                <div className="px-1">
                  <InlineEdit
                    value={project.name}
                    onSave={name => { renameProject(project.id, name); setEditingProjectId(null) }}
                    onCancel={() => setEditingProjectId(null)}
                  />
                </div>
              ) : (
                <div
                  className={cn(
                    'flex items-center gap-1 rounded-md transition-colors',
                    selectedProjectId === project.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-foreground/70 hover:bg-muted/60 hover:text-foreground'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setActiveProjectId(project.id)
                      setActiveConversation(null)
                      navigate(`/p/${encodeURIComponent(project.id)}`)
                    }}
                    aria-current={selectedProjectId === project.id ? 'page' : undefined}
                    className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm"
                  >
                    <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate flex-1">{project.name}</span>
                  </button>
                  {project.id !== 'default' && !serverProjectIds.has(project.id) && (
                    <span className="flex shrink-0 items-center gap-0.5 pr-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        aria-label={`Rename project ${project.name}`}
                        title={`Rename project ${project.name}`}
                        onClick={() => setEditingProjectId(project.id)}
                        className="h-5 w-5 rounded text-muted-foreground hover:text-foreground flex items-center justify-center"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete project ${project.name}`}
                        title={`Delete project ${project.name}`}
                        onClick={() => deleteProject(project.id)}
                        className="h-5 w-5 rounded text-muted-foreground hover:text-destructive flex items-center justify-center"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}
              {selectedProjectId === project.id && (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(`/p/${project.id}/knowledge`)}
                    className={cn(
                      'flex items-center gap-2 w-full ml-4 pl-2 pr-2 py-1 rounded-md text-[13px] transition-colors',
                      location.pathname === `/p/${project.id}/knowledge`
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <Brain className="h-3 w-3 shrink-0" />
                    Knowledge
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/p/${project.id}/skills`)}
                    className={cn(
                      'flex items-center gap-2 w-full ml-4 pl-2 pr-2 py-1 rounded-md text-[13px] transition-colors',
                      location.pathname.endsWith('/skills')
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <Sparkles className="h-3 w-3 shrink-0" />
                    Playbooks
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/p/${project.id}/knowledge/sources`)}
                    className={cn(
                      'flex items-center gap-2 w-full ml-4 pl-2 pr-2 py-1 rounded-md text-[13px] transition-colors',
                      location.pathname.endsWith('/knowledge/sources')
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <Database className="h-3 w-3 shrink-0" />
                    Sources
                  </button>
                  <Link
                    to={`/p/${encodeURIComponent(project.id)}/workitems`}
                    className={cn(
                      'flex items-center gap-2 w-full ml-4 pl-2 pr-2 py-1 rounded-md text-[13px] transition-colors',
                      location.pathname.endsWith('/workitems')
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <Briefcase className="h-3 w-3 shrink-0" />
                    Work
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Conversations list */}
      <div className="flex-1 overflow-y-auto px-2 pt-3 pb-2">
        <div className="px-1 mb-1">
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Conversations</span>
        </div>
        {projectConversations.length === 0 ? (
          <div className="px-2 py-4 text-xs text-muted-foreground text-center">
            No conversations yet
          </div>
        ) : (
          <div className="space-y-0.5">
            {projectConversations.map(convo => (
              <div key={convo.id} className="group">
                {editingConvoId === convo.id ? (
                  <div className="px-1">
                    <InlineEdit
                      value={convo.title}
                      onSave={title => { renameConversation(convo.id, title); setEditingConvoId(null) }}
                      onCancel={() => setEditingConvoId(null)}
                    />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex items-center gap-1 rounded-md transition-colors',
                      activeConversationId === convo.id
                        ? 'bg-muted/80 text-foreground font-medium'
                        : 'text-foreground/70 hover:bg-muted/60 hover:text-foreground'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveConversation(convo.id)
                        navigate(`/c/${convo.id}`, { replace: true })
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm"
                    >
                      <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate flex-1">{convo.title}</span>
                    </button>
                    <span className="flex shrink-0 items-center gap-0.5 pr-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        type="button"
                        aria-label={`Rename conversation ${convo.title}`}
                        title={`Rename conversation ${convo.title}`}
                        onClick={() => setEditingConvoId(convo.id)}
                        className="h-5 w-5 rounded text-muted-foreground hover:text-foreground flex items-center justify-center"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete conversation ${convo.title}`}
                        title={`Delete conversation ${convo.title}`}
                        onClick={() => deleteConversation(convo.id)}
                        className="h-5 w-5 rounded text-muted-foreground hover:text-destructive flex items-center justify-center"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-2 py-2">
        <UserMenu onNavigate={navigate} />
      </div>

    </aside>
  )
}
